import React from 'react';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import MetricListContainer from 'src/components/BeaconV2Components/MetricList/MetricListContainer';
import { formatTime } from 'src/utils/dateformatting';
import AdvancedSearchRequestBuilder from 'src/components/BeaconV2Components/utils/AdvancedSearchRequestBuilder';
import _startCase from 'lodash/startCase';
import _get from 'lodash/get';
import numeral from 'numeral';
import { useMainEntityId, useMainEntityType } from 'src/utils/Hooks/reduxSelectors';
import { OptimizationGridHeader } from './OptimizationGridHeader';
import { useAppSelector } from 'src/utils/Hooks';

const actionToDisplayValueMapping: { [action: string]: string } = {
  increase: 'Increase',
  decrease: 'Decrease',
  update: 'Update',
  nochange: 'Unchanged',
  enable: 'Enabled',
  pause: 'Paused',
  paused: 'Paused',
  changed: 'Changed'
};

const changeSourceDescriptionMap: any = {
  manualInAdManager: 'manually through Beacon',
  automationV2: 'by Beacon automation',
  external: 'outside of Beacon',
  scheduledAction: 'through a Beacon scheduled action'
};

const changeActionDescriptionMap: any = {
  increase: 'increased',
  decrease: 'decreased',
  pause: 'paused',
  enable: 'enabled',
  changed: 'changed'
};

interface ChangeHistoryDoc {
  _id: string;
  date: string;
  dayId: number;
  weekId: number;
  entityId: string;
  entityIdApi: string;
  retailerId: number;
  beaconClientId: number;
  beaconClientLoginId: number;
  adGroupId?: string;
  adGroupName?: string;
  changeDataType: string;
  changeType: string;
  changeSource: string;
  changeActionDescription: string;
  changeReason: string;
  changeAction: string;
  campaignId?: string;
  campaignIdApi?: string;
  campaignType?: string;
  campaignName?: string;
  oldValue?: string;
  newValue?: string;
  bidSavings?: number;
  optimizationStrategy?: string;
  searchKeyword?: string;
  searchKeywordFuzzy?: string;
  matchingType?: string;
  portfolioId?: string;
  portfolioIdApi?: string;
  productId?: string;
  portfolioName?: string;
  targetId?: string;
  updatedTime: string;
}

const buildChangeReason = ({
  changeActionDescription,
  changeReason,
  changeSource,
  changeType
}: ChangeHistoryDoc): string => {
  if (changeSource === 'automationV2') {
    return changeReason;
  }
  const origin = changeSourceDescriptionMap[changeSource] || changeSource;
  const action = changeActionDescriptionMap[changeActionDescription] || changeActionDescription;
  return `${changeType} ${action} ${origin}`;
};

export const OptimizationTableGroupByFields = [
  {
    displayName: 'Target Optimizations',
    name: 'target'
  },
  {
    displayName: 'Campaign Optimizations',
    name: 'campaign'
  },
  {
    displayName: 'Manual Optimizations',
    name: 'manual'
  }
];

const ChangeReasonField = {
  displayName: 'Reason',
  name: 'changeReason',
  headerName: 'Reason',
  valueFormatter: (_, row) => {
    return (
      <div style={{ width: '232px' }}>
        <Text variant="body2">{buildChangeReason(row)}</Text>
      </div>
    );
  }
};

const ChangedOnField = {
  displayName: 'Date',
  name: 'date',
  headerName: 'Changed On',
  valueFormatter: (_, row) => {
    return <Text variant="body2">{formatTime(row.date, 'MMM D, YYYY')}</Text>;
  }
};

export const CampaignNameColumnDefinition = {
  headerName: 'Campaign',
  valueFormatter: (_, row) => {
    return <Text variant="body2">{row.campaignName}</Text>;
  }
};

export const ManualHeaderColumnDefinition = {
  headerName: 'Target',
  valueFormatter: (_, row) => {
    const mappingTable = {
      campaign: {
        routeName: 'adCampaign',
        fieldName: 'campaignName',
        entityId: 'campaignId'
      },
      target: {
        routeName: 'adTarget',
        fieldName: 'searchKeyword',
        entityId: 'searchKeyword'
      },
      keyword: {
        routeName: 'adTarget',
        fieldName: 'searchKeyword',
        entityId: 'searchKeyword'
      },
      portfolio: {
        routeName: 'adPortfolio',
        fieldName: 'portfolioName',
        entityId: 'portfolioId'
      },
      product: {
        routeName: 'product',
        fieldName: 'productId',
        entityId: 'stacklineSku'
      },
      adGroup: {
        routeName: 'adGroup',
        fieldName: 'adGroupName',
        entityId: 'adGroupId'
      }
    };

    const changeDataType = _get(row, ['changeDataType'], 'campaign');

    const campaignItem = mappingTable[changeDataType];
    const entityId = _get(row, [campaignItem.entityId]);
    const displayName = _get(row, [campaignItem.fieldName]);
    // const { routeName } = campaignItem;

    // if (entityId) {
    //   const link = `/${routeName}/${entityId}${searchParams}&${queryString.stringify(parsedAdditionalParameters)}`;
    //   return <Link to={link}>{displayName || entityId}</Link>;
    // } else
    // {
    return <div style={{ width: '232px' }}>{displayName || entityId}</div>;
    // }
  }
};

export const TargetColumnDefinition = {
  headerName: 'Target',
  valueFormatter: (_, row) => {
    return <Text variant="body2">{row.searchKeyword}</Text>;
  }
};

export const MatchTypeColumnDefinition = {
  headerName: 'Match Type',
  valueFormatter: (_, row) => {
    return <Text variant="body2">{row.matchingType}</Text>;
  }
};

export const ActionColumnDefinition = {
  headerName: 'Action',
  valueFormatter: (_, row) => {
    return (
      <Text variant="body2">{`${_startCase(row.changeType)} ${
        actionToDisplayValueMapping[row.changeActionDescription]
      }`}</Text>
    );
  }
};

export const PriorValueColumnDefinition = {
  headerName: 'Prior Value',
  valueFormatter: (_, row) => {
    return (
      <Text variant="body2">
        {!Number.isNaN(+row.oldValue) ? numeral(+row.oldValue || 0).format('$1,000.00') : row.oldValue}
      </Text>
    );
  }
};

export const NewValueColumnDefinition = {
  headerName: 'New Value',
  valueFormatter: (_, row) => {
    return (
      <Text variant="body2">
        {!Number.isNaN(+row.newValue) ? numeral(+row.newValue || 0).format('$1,000.00') : row.newValue}
      </Text>
    );
  }
};

const gridConfigByOptimizationType = {
  target: {
    buildRequest: (builder: AdvancedSearchRequestBuilder) => {
      return builder
        .addConditionTermFilter('changeDataType', 'should', ['keyword', 'target'])
        .addConditionTermFilter('changeSource', 'should', ['automationV2', 'scheduledAction']);
    },
    headColumnDefinition: TargetColumnDefinition,
    additionalColumnDefs: [
      MatchTypeColumnDefinition,
      ActionColumnDefinition,
      PriorValueColumnDefinition,
      NewValueColumnDefinition,
      ChangeReasonField,
      ChangedOnField
    ]
  },
  campaign: {
    buildRequest: (builder: AdvancedSearchRequestBuilder) => {
      return builder.addConditionTermFilter('changeDataType', 'should', ['campaign']);
    },
    headColumnDefinition: CampaignNameColumnDefinition,
    additionalColumnDefs: [
      ActionColumnDefinition,
      PriorValueColumnDefinition,
      NewValueColumnDefinition,
      ChangeReasonField,
      ChangedOnField
    ]
  },
  manual: {
    buildRequest: (builder: AdvancedSearchRequestBuilder) => {
      return builder
        .addConditionTermFilter('changeDataType', 'should', [
          'campaign',
          'portfolio',
          'product',
          'keyword',
          'target',
          'adGroup'
        ])
        .addConditionTermFilter('changeSource', 'should', ['manualInAdManager', 'external']);
    },
    headColumnDefinition: ManualHeaderColumnDefinition,
    additionalColumnDefs: [
      ActionColumnDefinition,
      PriorValueColumnDefinition,
      NewValueColumnDefinition,
      ChangeReasonField,
      ChangedOnField
    ]
  }
};

const applyEntityIdTermFilter = (
  entityType: string,
  entityId: string | number,
  requestBuilder: AdvancedSearchRequestBuilder
) => {
  if (entityType === 'product') {
    requestBuilder.addConditionTermFilter('stacklineSku', 'should', [entityId]);
  } else if (entityType === 'brand') {
    requestBuilder.addConditionTermFilter('brandId', 'should', [entityId]);
  } else if (entityType === 'category') {
    requestBuilder.addConditionTermFilter('categoryId', 'should', [entityId]);
  } else if (entityType === 'subcategory') {
    requestBuilder.addConditionTermFilter('subCategoryId', 'should', [entityId]);
  } else if (entityType === 'adEntity') {
    requestBuilder.addConditionTermFilter('entityId', 'should', [entityId]);
  } else if (entityType === 'adPortfolio') {
    requestBuilder.addConditionTermFilter('portfolioId', 'should', [entityId]);
  } else if (entityType === 'adCampaign') {
    requestBuilder.addConditionTermFilter('campaignId', 'should', [entityId]);
  }
  return requestBuilder;
};

// action, prior value, new value, reason, date

const OptimizationsGrid = ({
  defaultOptimizationType
}: {
  defaultOptimizationType: 'target' | 'campaign' | 'manual';
}) => {
  const entityType = useMainEntityType();
  const entityId = useMainEntityId();
  const [optimizationType, setOptimizationType] = React.useState(defaultOptimizationType);
  const { startDayId, endDayId } = useAppSelector((state) => state.mainTimePeriod);

  const buildRequest = (builder: AdvancedSearchRequestBuilder) => {
    if (entityId) {
      applyEntityIdTermFilter(entityType, entityId, builder);
    }
    return gridConfigByOptimizationType[optimizationType]
      .buildRequest(builder)
      .removeConditionTermFilters((builderFilter) => builderFilter.fieldName === 'categoryId')
      .removeConditionRangeFilters((builderFilter) => builderFilter.fieldName === 'weekId')
      .addConditionRangeFilter('dayId', startDayId, endDayId)
      .updateDoAggregation(false)
      .updateReturnDocuments(true)
      .clearSortFields()
      .addSortFieldWithOnlyFieldName('updatedTime', 'desc')
      .setPageSize(100)
      .modifyAggregation(
        (aggBuilder) =>
          aggBuilder
            .addConditionRangeFilter('dayId', startDayId, endDayId)
            .removeConditionTermFilter('weekId')
            .removeConditionRangeFilters('weekId')
            .clearComparisonRangeFilters() // comparison range filters are not needed
      );
  };

  return (
    <>
      <OptimizationGridHeader
        options={OptimizationTableGroupByFields}
        setOption={setOptimizationType}
        selectedOption={optimizationType}
      />
      <MetricListContainer
        headColumnDefinition={gridConfigByOptimizationType[optimizationType].headColumnDefinition}
        skeletonRows={20}
        entityTableHeaderProps={{}}
        showHeader={false}
        indexName="adOptimizationHistory"
        fields={[]}
        additionalColumnDefs={gridConfigByOptimizationType[optimizationType].additionalColumnDefs}
        useTableDataProps={{
          buildRequest,
          groupByFieldName: 'date',
          itemsPerPage: 10
        }}
        showGrid={false}
        sortFieldName="changeAction"
      />
    </>
  );
};

export default OptimizationsGrid;
